import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { chartColors } from '@/utils/chartColors';
import { useRsColors } from '@/hooks/useRsColors';
import { getDateXAxisConfig, getSmartYAxisConfig, isDateLabel, parseDateLiteral, formatDateLabel, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { getChartFontSize } from '@/utils/chartTypography';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { getDefaultPreviousYearWindowFromLabels } from '@/utils/chartWindow';
import { shouldShowDataLabelsByFrequency } from '@/utils/chartDataLabelPolicy';
import { min } from 'date-fns';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export interface StackedDataset {
  data: number[];
  label: string;
  color?: string;
  dataLabelColor?: string;
}

interface StackedBarChartProps {
  datasets: StackedDataset[];
  labels: string[];
  className?: string;
  percentage?: boolean;
  showRangeSlider?: boolean;
  showDataLabels?: boolean;
  plugins?: any[];
  hideLegend?: boolean;
}

const StackedBarChart: React.FC<StackedBarChartProps> = ({
  datasets,
  labels,
  className,
  percentage = false,
  showRangeSlider = true,
  showDataLabels = true,
  plugins = [],
  hideLegend = false,
}) => {
  const rs = useRsColors();

  const defaultDateWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(labels),
    [labels],
  );

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    defaultStartIndex: defaultDateWindow.startIndex,
    defaultEndIndex: defaultDateWindow.endIndex,
  });

  const visibleLabels = useMemo(
    () => (showRangeSlider ? labels.slice(startIndex, endIndex + 1) : labels),
    [showRangeSlider, labels, startIndex, endIndex],
  );

  const visibleDatasets = useMemo(
    () => (showRangeSlider
      ? datasets.map((dataset) => ({ ...dataset, data: dataset.data.slice(startIndex, endIndex + 1) }))
      : datasets),
    [showRangeSlider, datasets, startIndex, endIndex],
  );

  const shouldShowLabels = useMemo(
    () => shouldShowDataLabelsByFrequency({
      enabled: true,
      labels: visibleLabels,
      maxDailyWindowDays: 365,
    }),
    [visibleLabels],
  );

  const chartDatasets = visibleDatasets.map((ds, i) => ({
    label: ds.label,
    data: ds.data,
    backgroundColor: ds.color || chartColors.getColorByIndex(i),
    borderWidth: 0,
    stack: 'stack',
    datalabels: ds.dataLabelColor ? { color: ds.dataLabelColor } : undefined,
  }));

  const stackedTotals = useMemo(() => {
    if (!visibleDatasets.length) return [] as number[];
    const pointCount = Math.max(...visibleDatasets.map((ds) => ds.data.length), 0);
    return Array.from({ length: pointCount }, (_, index) =>
      visibleDatasets.reduce((sum, ds) => sum + (Number(ds.data[index]) || 0), 0),
    );
  }, [visibleDatasets]);

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, right: 12, bottom: 8, left: 8 },
    },
    plugins: {
      legend: {
        display: !hideLegend,
        position: 'top' as const,
        labels: {
          color: rs.offWhite || '#f3f4f6',
          usePointStyle: true,
          pointStyle: 'rect',
          font: { size: getChartFontSize() },
        },
      },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (items: any[]) => {
            if (!items.length) return '';
            const raw = items[0].label;
            if (isDateLabel(raw)) return formatDateLabel(parseDateLiteral(raw));
            return raw;
          },
        },
      },
      datalabels: {
        // Show labels whenever the visible window spans ≤ 3 years (anywhere on the timeline)
        display: showDataLabels && isWithinThreeYearRange(visibleLabels),
        color: (context: any) => context.dataset?.datalabels?.color || rs.offWhite || '#f3f4f6',
        anchor: 'center',
        align: 'center',
        font: { size: getChartFontSize() },
        formatter: (value: number) => {
          if (value === null || value === undefined) return '';
          if (value === 0) return '';
          const numValue = Number(value);
          if (numValue < 1 && numValue > -1) {
            return numValue.toFixed(1);
          }
          return numValue.toFixed(0);
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          maxRotation: 0,
          minRotation: 0,
          callback: function (_value: any, index: number) {
            const label = visibleLabels[index];
            if (!label) return '';
            const d = parseDateLiteral(String(label));
            // <3yr window → every 6 months (MMM yyyy); dragged past 3yr → year only
            if (!isNaN(d.getTime())) {
              return inventoryChangeAxisLabel(d, isPastThreeYearMark(visibleLabels));
            }
            return index % 6 === 0 ? label : '';
          },
        },
        grid: { color: rs.gridLine },
      },
      y: {
        stacked: true,
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          ...(percentage ? { callback: (v: any) => `${v}%` } : {}),
        },
        grid: { color: rs.gridLine },
        ...(percentage
          ? { max: 100 }
          : getSmartYAxisConfig(stackedTotals.filter((v): v is number => Number.isFinite(v)))),
          min: 0,
      },
    },
  };

  return (
    <div className={className || 'w-full h-full bg-rs-jet rounded'} data-testid="stacked-bar-chart">
      <div className={showRangeSlider && hasWindowing ? 'h-[calc(100%-50px)] w-full' : 'h-full w-full'}>
        <Bar data={{ labels: visibleLabels, datasets: chartDatasets }} options={options} plugins={plugins} />
      </div>
      {showRangeSlider && hasWindowing && (
        <ChartRangeSlider
          totalPoints={labels.length}
          startIndex={startIndex}
          endIndex={endIndex}
          onChange={setWindow}
        />
      )}
    </div>
  );
};

export default StackedBarChart;